import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MailCheck, RefreshCw, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

const CODE_LEN = 6;

export default function VerifyEmail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { verifyEmail, resendCode } = useAuth();
  const email = state?.email || new URLSearchParams(window.location.search).get('email') || '';
  const [digits, setDigits] = useState(Array(CODE_LEN).fill(''));
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(60);
  const refs = useRef([]);

  useEffect(() => { if (!email) navigate('/start'); }, [email, navigate]);
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);
  useEffect(() => { refs.current[0]?.focus(); }, []);

  const code = digits.join('');

  const submit = async (value = code) => {
    if (value.length !== CODE_LEN || busy) return;
    setBusy(true); setError(''); setInfo('');
    const result = await verifyEmail(email, value);
    setBusy(false);
    if (result.success) {
      navigate(result.userType === 'candidate' ? '/candidate/profile' : '/dashboard', { replace: true });
    } else {
      setError(result.error || t('verifyEmail.invalid'));
      setDigits(Array(CODE_LEN).fill(''));
      refs.current[0]?.focus();
    }
  };

  const onChange = (i, v) => {
    const clean = v.replace(/\D/g, '');
    if (!clean) { setDigits((d) => { const n = [...d]; n[i] = ''; return n; }); return; }
    const next = [...digits];
    clean.split('').slice(0, CODE_LEN - i).forEach((ch, k) => { next[i + k] = ch; });
    setDigits(next);
    const last = Math.min(i + clean.length, CODE_LEN - 1);
    refs.current[last]?.focus();
    if (next.every((d) => d)) submit(next.join(''));
  };

  const onKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) refs.current[i - 1]?.focus();
    if (e.key === 'Enter') submit();
  };

  const resend = async () => {
    setError(''); setInfo('');
    const r = await resendCode(email);
    if (r.success) { setInfo(t('verifyEmail.resent')); setCooldown(60); }
    else setError(r.error);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12" data-testid="verify-email-page">
      <Card className="w-full max-w-md p-8">
        <div className="w-14 h-14 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center mb-5"><MailCheck className="h-7 w-7" /></div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">{t('verifyEmail.title')}</h1>
        <p className="text-slate-600 text-sm mb-6">
          {t('verifyEmail.sentTo')} <strong className="text-slate-900 break-all" data-testid="verify-email-address">{email}</strong>. {t('verifyEmail.hint')}
        </p>

        <div className="flex justify-between gap-2 mb-5" data-testid="verify-code-inputs">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => { refs.current[i] = el; }}
              value={d}
              onChange={(e) => onChange(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
              onFocus={(e) => e.target.select()}
              inputMode="numeric"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              maxLength={CODE_LEN}
              aria-label={t('verifyEmail.digit', { n: i + 1 })}
              className="w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-bold rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:outline-none bg-white"
              data-testid={`verify-digit-${i}`}
            />
          ))}
        </div>

        {error && <Alert variant="destructive" className="mb-4" data-testid="verify-error"><AlertDescription>{error}</AlertDescription></Alert>}
        {info && <p className="text-sm text-emerald-700 mb-4" data-testid="verify-info">{info}</p>}

        <Button onClick={() => submit()} disabled={busy || code.length !== CODE_LEN} className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-base" data-testid="verify-submit-btn">
          {busy ? t('verifyEmail.checking') : t('verifyEmail.confirm')}
        </Button>

        <div className="mt-5 flex items-center justify-between text-sm">
          <button type="button" onClick={resend} disabled={cooldown > 0} className="inline-flex items-center gap-1.5 text-blue-700 disabled:text-slate-400 font-medium" data-testid="verify-resend-btn">
            <RefreshCw className="h-4 w-4" />{cooldown > 0 ? t('verifyEmail.resendIn', { s: cooldown }) : t('verifyEmail.resend')}
          </button>
          <Link to="/start" className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800" data-testid="verify-back-link"><ArrowLeft className="h-4 w-4" />{t('verifyEmail.back')}</Link>
        </div>
        <p className="mt-6 text-xs text-slate-500">{t('verifyEmail.spam')}</p>
      </Card>
    </div>
  );
}
